/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  Box,
  CssBaseline,
  Container,
  Typography,
  Grid,
  CircularProgress,
  IconButton,
} from "@material-ui/core";
import { useCallback, useEffect, useMemo, useState } from "react";
import { CustomTabs } from "../components-molecules/Tabs";
import { CustomTab } from "../components-molecules/Tab";
import { collectionOrdersStyles } from "../utils/styles/collectionOrdersStyles";
import SearchRoundedIcon from "@material-ui/icons/SearchRounded";
import InfiniteScroll from "react-infinite-scroll-component";
import { MerchantSubscription, ProTeamSubscription, activateProSubscription, addProTeamDeliveryTranche, getMerchantSubscriptionList, getProSubscriptionList, suspendProTeamSubscription } from "../redux/actions/subscriptions";
import { HeaderWithButtons, Searchbar } from "../components-molecules";
import SubscriptionCardMerchant from "../components-molecules/SubscriptionCardMerchant";
import SubscriptionCardPro from "../components-molecules/SubscriptionCardPro";
import { useSelector } from "react-redux";
import { useAppWebSocket } from "../hooks/useAppWebSocket";
import { useHistory, useLocation } from "react-router-dom";
import { useTranslation } from 'react-i18next';

const pageSize = 50;

type UpdateType = "MerchantSubscriptionsList" | "ProTeamSubscriptionsList" | "ShopSubscriptionsList";

export interface DataUpdateSocketMessage {
  LastUpdate: string;
  UpdateType: UpdateType;
}

export function Subscriptions() {
  const { t } = useTranslation();
  const location = useLocation();
  const history = useHistory();

  const classes = collectionOrdersStyles();
  const SUBSCRIPTIONS_TABS = [t('admin.merchantsTab'), t('admin.tkProOrganisations')];
  const [merchants, setMerchants] = useState<MerchantSubscription[]>([]);
  const [proOrganisations, setProOrganisations] = useState<ProTeamSubscription[]>([]);
  const userData = useSelector((state: any) => state.login);
  const isAdmin = useMemo(() => userData.role === "Admin", [userData]);
  const [listLastUpdate, setListLastUpdate] = useState<string>();

  const [loading, setLoading] = useState(false);
  const [merchantTotal, setMerchantTotal] = useState(0);
  const [proTotal, setProTotal] = useState(0);

  // URL Filters
  const urlParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const page = useMemo(() => Number(urlParams.get('page')), [location.search]) || 0;
  const query = useMemo(() => urlParams.get('query'), [location.search]) || '';
  const searchOpen = useMemo(() => urlParams.get('searchOpen') === 'true', [location.search]) || false;
  const tabIndex = useMemo(() => Number(urlParams.get('tabIndex')), [location.search]) || 0;

  const setParam = (key: string, value: any) => {
    urlParams.delete(key);
    if (value !== null && value !== undefined) {
      if (Array.isArray(value)) {
        value.forEach((v) => {
          urlParams.append(key, v);
        });
      } else {
        urlParams.set(key, value);
      }
      history.push({ pathname: location.pathname, search: urlParams.toString() });
    }
  }

  const deleteParam = (key: string) => {
    urlParams.delete(key);
    history.push({ pathname: location.pathname, search: urlParams.toString() });
  }

  const setPage = (p: number) => {
    setParam('page', p);
  }

  const { lastJsonMessage } = useAppWebSocket<DataUpdateSocketMessage | null>(
    '/ws/data-update',
    { }
  );

  const getMerchants = (currentPage = 0) => {
    getMerchantSubscriptionList(
      isAdmin,
      {
        Page: currentPage,
        Size: pageSize,
        Query: query
      },
      (res) => {
        if (currentPage === 0) {
          setMerchantTotal(res.Total);
          setMerchants(res.Subscriptions);
        }
        else {
          setMerchants((prev) => ([
            ...prev,
            ...res.Subscriptions
          ]));
        }
        setLoading(false);
      }, (err) => {
        // console.log('Err', err) - removed
      });
  };

  const getProOrganisations = (currentPage = 0) => {
    getProSubscriptionList({
      Page: currentPage,
      Size: pageSize,
      Query: query
    },
      (res) => {
        if (currentPage === 0) {
          setProTotal(res.Total);
          setProOrganisations(res.Subscriptions);
        }
        else {
          setProOrganisations((prev) => ([
            ...prev,
            ...res.Subscriptions
          ]));
        }
        setLoading(false);
      }, (err) => {
        // console.log('Err', err) - removed
      });
  };

  const refreshList = () => {
    tabIndex === 0 ? getMerchants() : getProOrganisations();
  };

  useEffect(() => {
    setPage(0);
  }, []);

  useEffect(() => {
    if (lastJsonMessage && lastJsonMessage.LastUpdate !== listLastUpdate) {
      setListLastUpdate(lastJsonMessage.LastUpdate);
      if (lastJsonMessage.UpdateType === "MerchantSubscriptionsList" && tabIndex === 0) getMerchants();
      if (lastJsonMessage.UpdateType === "ProTeamSubscriptionsList" && tabIndex === 1) getProOrganisations();
    }
  }, [lastJsonMessage, lastJsonMessage?.LastUpdate, lastJsonMessage?.UpdateType]);

  useEffect(() => {
    refreshList();
  }, [query, tabIndex]);

  const loadMore = () => {
    tabIndex === 0 ? getMerchants(page + 1) : getProOrganisations(page + 1);
    setPage(page + 1);
  };

  const onTabChange = (_: number, newValue: number) => {
    if (newValue !== Number(tabIndex)) {
      setParam('tabIndex', newValue);
      setPage(0);
      setLoading(true);
      setProOrganisations([]);
      setMerchants([]);
    }
  };

  const getTabText = useCallback(() => {
    const text = "You don't have any";
    switch (tabIndex) {
      case 0:
        return `${text} merchants`;
      case 1:
        return `${text} TK Pro Organisations`;
    }
  }, [tabIndex]);

  const renderTabs = () => {
    return (
      <Container className={classes.tabContainer}>
        <CustomTabs value={tabIndex} onChange={onTabChange}>
          {SUBSCRIPTIONS_TABS.map((tab: string) => (
            <CustomTab key={tab} label={tab} />
          ))}
        </CustomTabs>
      </Container>
    );
  };

  const renderHeader = () => {
    return (
      <HeaderWithButtons title="Subscriptions">
        <HeaderWithButtons.ButtonArea>
          <IconButton
            data-automation="search-button"
            className={classes.searchIconButton}
            color="primary"
            onClick={() => searchOpen ? deleteParam('searchOpen') : setParam('searchOpen', true)}
          >
            <SearchRoundedIcon
              color="primary"
              className={classes.searchIcon}
            />
          </IconButton>
        </HeaderWithButtons.ButtonArea>
      </HeaderWithButtons>
    );
  };

  return (
    <Container className={classes.root}>
      {renderHeader()}
      {renderTabs()}
      {!loading &&
        <Searchbar
          query={query}
          isOpen={searchOpen}
          placeholder="Search subscriptions"
          onChange={(e) => {
            setParam('query', e.target.value);
          }}
          onCancel={() => {
            deleteParam('query');
            deleteParam('searchOpen');
          }}
        />
      }
      <Box mt={3}>
        {query.length > 0 && ((tabIndex === 0 && merchants.length === 0) || (tabIndex === 1 && proOrganisations.length === 0)) ?
          <Grid container item className={classes.uPaddingLeft} style={{ marginTop: '40px' }}>
            <Grid container item style={{ maxWidth: '320px' }}>
              <Typography variant="h6" component="span">
                0 results found
              </Typography>
              <Typography
                variant="body2"
                color="textSecondary"
                component="span"
                style={{ marginTop: '12px' }}>
                Adjust your filtering and try again
              </Typography>
            </Grid>
          </Grid>
          : null}
        {loading && page === 1 ? (
          <Grid
            item
            container
            justifyContent="center"
            alignItems="center"
            style={{ marginTop: "96px" }}
          >
            <CircularProgress />
          </Grid>
        ) : tabIndex === 0 ? (
          <InfiniteScroll
            style={{ overflow: "hidden" }}
            dataLength={merchants.length}
            next={() => loadMore()}
            scrollThreshold={0.8}
            hasMore={merchantTotal > merchants.length}
            loader={
              <Container style={{ width: "100%", display: "flex", justifyContent: "center" }}>
                <CircularProgress />
              </Container>
            }>
            {merchants?.map((o, i) => <SubscriptionCardMerchant key={o.MerchantId} refreshList={refreshList} {...o} />)}
          </InfiniteScroll>
        ) : tabIndex === 1 ? (
          <InfiniteScroll
            style={{ overflow: "hidden" }}
            dataLength={proOrganisations.length}
            next={() => loadMore()}
            scrollThreshold={0.8}
            hasMore={proTotal > proOrganisations.length}
            loader={
              <Container style={{ width: "100%", display: "flex", justifyContent: "center" }}>
                <CircularProgress />
              </Container>
            }>
            {proOrganisations?.map((o, i) => <SubscriptionCardPro key={o.ProTeamId} refreshList={refreshList}{...o} />)}
          </InfiniteScroll>
        ) : (
          <Grid
            container
            item
            justifyContent="center"
            alignItems="center"
            style={{ marginTop: "96px" }}
          >
            <Grid
              container
              item
              justifyContent="center"
              alignItems="center"
              style={{ maxWidth: "320px" }}
            >
              <Typography variant="h6" component="span" align="center">
                {getTabText()}
              </Typography>
            </Grid>
          </Grid>
        )}
      </Box>
    </Container>
  );
}

export default Subscriptions;
